/*-----------------------------------------------------------------------------*
 *  TOGGLER.JS
 *-----------------------------------------------------------------------------*
 *
 *  Toggle attributes and classes on DOM elements using only HTML attributes.
 *
 *  @author  Luca Rosaldi
 *  @version 1.0.0
 *
 */

/**
 * Toggler class.
 *
 * @param  {String}  namespace  Namespace for attributes, default 'data-toggler'
 *
 * Available attributes:
 *
 * [data-toggler="{selector}"]    (default: current element)
 *                                CSS selector for element(s) to toggle. Also
 *                                accepts the keyword "parent",which targets the
 *                                parent of the current element.
 *
 * [data-toggler-attr="{name}"]   (default: 'class')
 *                                Attribute to toggle.
 *
 * [data-toggler-value="{value}"] (default: 'is-active')
 * [data-toggler-class="{value}"] (default: 'is-active')
 *                                Attribute value or CSS class(es) to toggle.
 *
 * [data-toggler-event="{event}"] (default: 'click')
 *                                Javascript event type to respond to. If set to
 *                                'mouseover', it will file again on 'mouseout'
 *                                (unless [data-toggler-once] is present).
 *
 * [data-toggler-text="{text}"]   (default: false)
 *                                Replace text on trigger when it is toggled.
 *
 * [data-toggler-once]            (default: false)
 *                                Only fire the first time the element is toggled.
 *
 * [data-toggler-prevent]         (default: false)
 *                                Prevent the default event.
 */
const Toggler = class {

  constructor( namespace ) {
    this.namespace = namespace || 'data-toggler';

    this._addEvent = this._addEvent.bind( this );
    this._removeEvent = this._removeEvent.bind( this );
    this._handleEvent = this._handleEvent.bind( this );

    this.init();
  }

  /**
   * Initialize class.
   *
   * @access public
   *
   * @return {Toggler}
   */
  init() {
    [].forEach.call( document.querySelectorAll( `[${this.namespace}]` ), ( element ) => this._addEvent( element ) );
    return this;
  };

  /**
   * Destroy class.
   *
   * @access public
   *
   * @return {Toggler}
   */
  destroy() {
    [].forEach.call( document.querySelectorAll( `[${this.namespace}]` ), ( element ) => this._removeEvent( element ) );
    return this;
  };

  /**
   * Refresh module.
   *
   * @access public
   *
   * @return {Toggler}
   */
  refresh() {
    return this.destroy().init();
  };

  /**
   * Toggle classes for a specific element.
   *
   * @access public
   *
   * @param  {Node}    trigger
   * @return {Toggler}
   */
  toggle( trigger ) {
    const targets = this.getTargets( trigger );

    const isPressed  = trigger.getAttribute( 'aria-pressed' );
    const isExpanded = trigger.getAttribute( 'aria-expanded' );

    const attr = trigger.getAttribute( `${this.namespace}-attr` );
    const value = trigger.getAttribute( `${this.namespace}-value` ) || trigger.getAttribute( `${this.namespace}-class` ) || 'is-active';
    const text = trigger.getAttribute( `${this.namespace}-text` );

    // toggle classes for all targets
    targets.forEach( ( target ) => {

      // remove attribute
      if ( attr && target.getAttribute( attr ) ) {
        target.removeAttribute( attr );
      }
      // set attribute
      else if ( attr ) {
        target.setAttribute( attr, value );
      }
      // toggle classes
      else {
        this.toggleClasses( target, value );
      }

    });

    // toggle aria attributes
    if ( isPressed )  trigger.setAttribute( 'aria-pressed',  ! JSON.parse( isPressed ) );
    if ( isExpanded ) trigger.setAttribute( 'aria-expanded', ! JSON.parse( isExpanded ) );

    // toggle active class for the trigger
    if ( targets.indexOf( trigger ) < 0 ) {
      trigger.classList.toggle( 'is-active' );
    }

    // replace text inside the trigger
    if ( text ) {
      trigger.setAttribute( `${this.namespace}-text`, trigger.innerText );
      trigger.innerText = text;
    }

    return this;
  }

  /**
   * Add event listener for trigger element.
   *
   * @access private
   *
   * @param  {Node}    element
   * @return {Toggler}
   */
  _addEvent( element ) {
    const eventType = element.getAttribute( `${this.namespace}-event` ) || 'click';
    const handler = this._handleEvent;

    element.addEventListener( eventType, handler );

    // add mouseout trigger for mouseover events
    if ( eventType === 'mouseover' ) {
      element.addEventListener( 'mouseout', handler );
    }

    return this;
  };

  /**
   * Remove event listener for trigger element.
   *
   * @access private
   *
   * @param  {Node}    element
   * @return {Toggler}
   */
  _removeEvent( element ) {
    const eventType = element.getAttribute( `${this.namespace}-event` ) || 'click';
    const handler = this._handleEvent;

    element.removeEventListener( eventType, handler );

    if ( eventType === 'mouseover' ) {
      element.removeEventListener( 'mouseout', handler );
    }

    return this;
  };

  /**
   * Handle events for trigger element.
   *
   * @access private
   *
   * @param  {Event}   event
   * @return {Toggler}
   */
  _handleEvent( event ) {
    if ( event.target.hasAttribute( `${this.namespace}-prevent` ) ) {
      event.preventDefault();
    }
    if ( event.target.hasAttribute( `${this.namespace}-once` ) ) {
      this._removeEvent( event.target );
    }

    return this.toggle( event.target );
  };

  /**
   * Get targets for a specific trigger.
   *
   * @param  {Node}        trigger
   * @return {Array<Node>}
   */
  getTargets( trigger ) {
    const selector = trigger.getAttribute( `${this.namespace}` );
    if ( ! selector ) {
      return [ trigger ];
    }
    if ( selector === 'parent' ) {
      return [ trigger.parentNode ];
    }
    return [].slice.call( document.querySelectorAll( selector ) );
  }

  /**
   * Helper for toggling multiple classes at once, as IE 10 and 11
   * do not support that feature in classList.
   *
   * @param  {Node}         element
   * @param  {Array|String} classes
   * @return {Node}         element
   */
  toggleClasses( element, classes = [] ) {

    // turn string into array of class names
    if ( typeof classes === 'string' ) {
      classes = classes.replace( ',', ' ' );
      classes = classes.split( /\s+/ );
    }

    classes.forEach( function( className ) {
      element.classList.toggle( className );
    });

    return element;
  }

}

export default Toggler;
